import api from './api';

function unwrapPayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.data !== undefined && data.data !== null) return data.data;
  return data;
}

/**
 * GET /api/billing/config — Stripe publishable key for Elements.
 */
export const getBillingConfig = async () => {
  const response = await api.get('/billing/config');
  return unwrapPayload(response.data);
};

/**
 * POST /api/billing/setup-intent — save card without charge.
 */
export const createSetupIntent = async ({ setAsDefault = true } = {}) => {
  const response = await api.post('/billing/setup-intent', {
    set_as_default: setAsDefault,
  });
  return unwrapPayload(response.data);
};

/**
 * POST /api/billing/payment-intent — generic one-off (optional use).
 */
export const createPaymentIntent = async (body) => {
  const response = await api.post('/billing/payment-intent', body);
  return unwrapPayload(response.data);
};

function buildPaymentSelectionBody({
  paymentMethodSelection,
  paymentMethodId,
  useDefaultPaymentMethod,
  savePaymentMethod,
  setAsDefault,
}) {
  const body = {};

  if (paymentMethodSelection === 'saved') {
    body.payment_method_selection = 'saved';
    if (useDefaultPaymentMethod) {
      body.use_default_payment_method = true;
    } else if (paymentMethodId) {
      body.payment_method_id = paymentMethodId;
    }
  } else if (paymentMethodSelection === 'new') {
    body.payment_method_selection = 'new';
    body.save_payment_method = savePaymentMethod !== false;
    if (setAsDefault != null) {
      body.set_as_default = setAsDefault;
    }
  } else {
    if (paymentMethodId) body.payment_method_id = paymentMethodId;
    if (useDefaultPaymentMethod) body.use_default_payment_method = true;
    if (savePaymentMethod != null) body.save_payment_method = savePaymentMethod;
    if (setAsDefault != null) body.set_as_default = setAsDefault;
  }

  return body;
}

function parseBillingApiResponse(response) {
  const root = response?.data ?? {};
  return {
    success: root.success !== false,
    data: unwrapPayload(root),
    subscription: root.subscription ?? null,
    message: root.message ?? null,
    code: root.code ?? null,
  };
}

/**
 * POST /api/envelope/{envelopeId}/create-send-payment
 */
export const createEnvelopeSendPayment = async (
  envelopeId,
  options = {}
) => {
  const {
    savePaymentMethod = true,
    setAsDefault = true,
    paymentMethodSelection,
    paymentMethodId,
    useDefaultPaymentMethod,
  } = options;

  const body = buildPaymentSelectionBody({
    paymentMethodSelection,
    paymentMethodId,
    useDefaultPaymentMethod,
    savePaymentMethod,
    setAsDefault,
  });

  const response = await api.post(`/envelope/${envelopeId}/create-send-payment`, body);
  return parseBillingApiResponse(response);
};

/**
 * GET /api/billing/payment-methods
 */
export const listPaymentMethods = async () => {
  const response = await api.get('/billing/payment-methods');
  return unwrapPayload(response.data);
};

/**
 * Normalize list/sync/confirm payment-methods payloads.
 */
export function extractPaymentMethods(data) {
  if (!data || typeof data !== 'object') {
    return { methods: [], defaultId: null };
  }
  const methods = data.payment_methods ?? data.paymentMethods ?? [];
  const defaultId =
    data.default_payment_method_id ?? data.defaultPaymentMethodId ?? null;
  return {
    methods: Array.isArray(methods) ? methods.filter(Boolean) : [],
    defaultId: defaultId || null,
  };
}

/**
 * POST /api/billing/payment-methods/confirm
 * Attach card to org customer after SetupIntent / PaymentIntent succeeds in the browser.
 */
export const confirmPaymentMethods = async ({
  setupIntentId,
  paymentIntentId,
  setAsDefault = true,
} = {}) => {
  const body = { set_as_default: setAsDefault };
  if (setupIntentId) body.setup_intent_id = setupIntentId;
  if (paymentIntentId) body.payment_intent_id = paymentIntentId;
  const response = await api.post('/billing/payment-methods/confirm', body);
  return unwrapPayload(response.data);
};

/**
 * POST /api/billing/payment-methods/sync
 * Re-list cards; with intent/session ids, attaches the card to the org customer first.
 */
export const syncPaymentMethods = async ({
  setupIntentId,
  paymentIntentId,
  checkoutSessionId,
  setAsDefault = true,
} = {}) => {
  const body = { set_as_default: setAsDefault };
  if (setupIntentId) body.setup_intent_id = setupIntentId;
  if (paymentIntentId) body.payment_intent_id = paymentIntentId;
  if (checkoutSessionId) body.checkout_session_id = checkoutSessionId;
  const response = await api.post('/billing/payment-methods/sync', body);
  return unwrapPayload(response.data);
};

/**
 * POST /api/billing/payment-methods/{id}/default
 */
export const setDefaultPaymentMethod = async (paymentMethodId) => {
  const response = await api.post(`/billing/payment-methods/${paymentMethodId}/default`);
  return unwrapPayload(response.data);
};

/**
 * DELETE /api/billing/payment-methods/{id}
 */
export const deletePaymentMethod = async (paymentMethodId) => {
  const response = await api.delete(`/billing/payment-methods/${paymentMethodId}`);
  return unwrapPayload(response.data);
};

/**
 * POST /api/billing/subscribe — in-app plan upgrade (not checkout-session redirect).
 * Do not use with plan_code "credit_pack" — packs use purchaseCreditPack.
 */
export const subscribeToPlan = async ({
  planCode,
  billingInterval,
  paymentMethodSelection,
  paymentMethodId,
  useDefaultPaymentMethod,
  savePaymentMethod = true,
  setAsDefault,
}) => {
  const body = {
    plan_code: planCode,
    ...buildPaymentSelectionBody({
      paymentMethodSelection,
      paymentMethodId,
      useDefaultPaymentMethod,
      savePaymentMethod,
      setAsDefault,
    }),
  };

  if (billingInterval) {
    body.billing_interval = billingInterval;
    body.interval = billingInterval;
  }

  const response = await api.post('/billing/subscribe', body);
  return parseBillingApiResponse(response);
};

/**
 * POST /api/billing/credit-packs/purchase — prepaid envelope packs (Payment Element flow).
 */
export const purchaseCreditPack = async ({
  packCode,
  paymentMethodSelection,
  paymentMethodId,
  useDefaultPaymentMethod,
  savePaymentMethod = true,
  setAsDefault,
}) => {
  const body = {
    pack_code: packCode,
    ...buildPaymentSelectionBody({
      paymentMethodSelection,
      paymentMethodId,
      useDefaultPaymentMethod,
      savePaymentMethod,
      setAsDefault,
    }),
  };

  const response = await api.post('/billing/credit-packs/purchase', body);
  return parseBillingApiResponse(response);
};

/**
 * POST /api/billing/sync-subscription-plan — reconcile plan after Stripe payment when plan_synced is false.
 */
export const syncSubscriptionPlan = async () => {
  const response = await api.post('/billing/sync-subscription-plan');
  return parseBillingApiResponse(response);
};

/**
 * POST /api/billing/checkout-session — hosted Checkout URL (not used for in-app purchase).
 */
export const createCheckoutSession = async ({
  planCode,
  billingInterval,
  successUrl,
  cancelUrl,
}) => {
  const body = {
    plan_code: planCode,
    success_url: successUrl,
    cancel_url: cancelUrl,
  };
  if (billingInterval) {
    body.billing_interval = billingInterval;
    body.interval = billingInterval;
  }
  const response = await api.post('/billing/checkout-session', body);
  return unwrapPayload(response.data);
};
